import { DateTime } from "luxon";
import { resolveInProgressStatusIds } from "../../LeadListPage/LeadListFilters/leadPresets";
import { LEAD_AUTO_FILTERS_DEFAULT } from "../leadAutoFiltersDefault";

// Le référentiel dit « En cours de traitement » ; le BO affiche « En cours »
// partout (page entreprise comprise).
export const statusLabel = (status) =>
  status.id === "IN_PROGRESS" ? "En cours" : status.name;

// Raccourcis de statut de la barre repliée. Filtrés à l'affichage sur leur
// présence dans LEAD_STATUS (référentiel runtime).
export const QUICK_STATUSES = [
  { id: "TO_DO", label: "À traiter", icon: "fa-bullseye" },
  { id: "IN_PROGRESS", label: "En cours", icon: "fa-angle-double-right" },
  { id: "DONE", label: "Convertis", icon: "fa-check" },
];

// Segment « Étape » du tiroir (PER-2428) ; les libellés servent aussi aux
// jetons. Chaque option est un patch des deux clés isEstimated / isFinalized :
// « Commencés » = première étape non terminée, « À finaliser » = première
// étape terminée mais pas la seconde, « Finalisés » = dossier complet.
export const ETAPE_OPTIONS = [
  { id: "ALL", label: "Tous", patch: { isEstimated: null, isFinalized: null } },
  { id: "EN_COURS", label: "Commencés", patch: { isEstimated: false, isFinalized: null } },
  { id: "ESTIMES", label: "À finaliser", patch: { isEstimated: true, isFinalized: false } },
  { id: "FINALISES", label: "Finalisés", patch: { isEstimated: null, isFinalized: true } },
];

const memePatch = (filters, patch) =>
  Object.entries(patch).every(([key, val]) =>
    Object.is(filters[key] ?? null, val ?? null),
  );

// Option d'étape correspondant à l'état ; `undefined` si l'état combine les
// deux clés autrement (ex. « non finalisés » seul, hérité d'avant l'étape 2).
export const activeEtapeOption = (filters) =>
  ETAPE_OPTIONS.find((o) => memePatch(filters, o.patch));

// Libellé de l'état d'étape pour le jeton, `null` si aucun filtre d'étape.
export const etapeLabel = (filters) => {
  const option = activeEtapeOption(filters);
  if (option) return option.id === "ALL" ? null : option.label;
  if (filters.isFinalized === false && filters.isEstimated === null) return "Non finalisés";
  if (filters.isFinalized === true) return "Finalisés";
  if (filters.isEstimated === true) return "À finaliser";
  if (filters.isEstimated === false) return "Commencés";
  return null;
};

// Segment « Agent » du tiroir (PER-2433) : état de l'agent de tarification
// sur la demande, tel que graal le porte. Chaque option est un patch de
// `agentEtats` ; « Sans agent » = jamais intervenu (« aucun » côté graal).
export const AGENT_OPTIONS = [
  { id: "ALL", label: "Tous", patch: { agentEtats: [] } },
  { id: "AUCUN", label: "Sans agent", patch: { agentEtats: ["aucun"] } },
  // Verdict rendu avant tout lancement (PER-2449).
  { id: "ELIGIBLE", label: "Éligible, à lancer", patch: { agentEtats: ["eligible"] } },
  { id: "NON_ELIGIBLE", label: "Non éligible", patch: { agentEtats: ["non_eligible"] } },
  { id: "ACTION", label: "Action attendue", patch: { agentEtats: ["attente_action"] } },
  { id: "TRAVAIL", label: "Agent au travail", patch: { agentEtats: ["en_file", "en_cours"] } },
  { id: "TERMINE", label: "Terminé", patch: { agentEtats: ["termine"] } },
  { id: "ECHEC", label: "Échec", patch: { agentEtats: ["echec"] } },
];

const memesEtats = (a = [], b = []) =>
  a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i]);

export const activeAgentOption = (filters) =>
  AGENT_OPTIONS.find((o) => memesEtats(filters.agentEtats || [], o.patch.agentEtats));

// Libellé du filtre agent pour le jeton, `null` si aucun filtre.
export const agentLabel = (filters) => {
  if (!filters.agentEtats || filters.agentEtats.length === 0) return null;
  const option = activeAgentOption(filters);
  return option ? option.label : filters.agentEtats.join(", ");
};

export const DEVIS_OPTIONS = [
  { id: "ALL", label: "Tous", patch: { devisDepose: null } },
  { id: "DEPOSE", label: "Déposé", patch: { devisDepose: true } },
  { id: "AUCUN", label: "Sans devis", patch: { devisDepose: false } },
];

export const activeDevisOption = (filters) =>
  DEVIS_OPTIONS.find((o) => memePatch(filters, o.patch));

export const devisLabel = (filters) => {
  if (filters.devisDepose === true) return "déposé";
  if (filters.devisDepose === false) return "aucun";
  return null;
};

// Le contrat graal veut des dates `YYYY-MM-DD` (pas d'ISO horodaté).
const jour = (dt) => dt.toFormat("yyyy-LL-dd");

export const PERIOD_SHORTCUTS_AUTO = [
  {
    id: "LAST_30_DAYS",
    label: "30 derniers jours",
    build: () => ({
      beginAt: jour(DateTime.now().minus({ days: 30 })),
      endAt: jour(DateTime.now()),
    }),
  },
  {
    id: "LAST_7_DAYS",
    label: "7 derniers jours",
    build: () => ({
      beginAt: jour(DateTime.now().minus({ days: 7 })),
      endAt: jour(DateTime.now()),
    }),
  },
  {
    id: "THIS_MONTH",
    label: "Ce mois-ci",
    build: () => ({
      beginAt: jour(DateTime.now().startOf("month")),
      endAt: jour(DateTime.now()),
    }),
  },
];

const shortcut = (id) => PERIOD_SHORTCUTS_AUTO.find((s) => s.id === id);

/**
 * Vues rapides. `build(ctx)` renvoie un PATCH partiel des champs pilotés.
 * ctx = { userId, myRole, LEAD_STATUS }
 */
export const LEAD_AUTO_PRESETS = [
  {
    id: "MY_IN_PROGRESS",
    label: "Mes leads en cours",
    icon: "fa-bullseye",
    isAvailable: (ctx) => !!ctx.userId && ctx.myRole !== "PARTNER",
    build: (ctx) => ({
      assignedTo: ctx.userId ? [ctx.userId] : [],
      statuses: resolveInProgressStatusIds(ctx.LEAD_STATUS),
    }),
  },
  {
    id: "TO_DO_FINALIZED",
    label: "À traiter, finalisés",
    icon: "fa-inbox",
    isAvailable: (ctx) => (ctx.LEAD_STATUS || []).some((s) => s.id === "TO_DO"),
    build: () => ({ statuses: ["TO_DO"], isFinalized: true }),
  },
  {
    // Première étape du formulaire terminée, pas la seconde : à relancer.
    id: "TO_DO_ESTIMATED",
    label: "À traiter, à finaliser",
    icon: "fa-hourglass-half",
    isAvailable: (ctx) => (ctx.LEAD_STATUS || []).some((s) => s.id === "TO_DO"),
    build: () => ({ statuses: ["TO_DO"], isEstimated: true, isFinalized: false }),
  },
  {
    id: "DEVIS_DEPOSE",
    label: "Devis déposé",
    icon: "fa-file-text-o",
    isAvailable: () => true,
    build: () => ({ devisDepose: true }),
  },
  {
    id: "LAST_30_DAYS",
    label: "30 derniers jours",
    icon: "fa-calendar",
    isAvailable: () => true,
    build: () => shortcut("LAST_30_DAYS").build(),
  },
];

const sameSet = (a = [], b = []) =>
  a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

/**
 * Un preset est actif ssi chaque clé de son patch correspond à l'état :
 * tableaux ordre-insensibles, scalaires comparés avec Object.is (`true`,
 * `false` et `null` sont trois valeurs distinctes pour isFinalized).
 */
export const isPresetActive = (preset, filters, ctx) =>
  Object.entries(preset.build(ctx)).every(([key, val]) =>
    Array.isArray(val)
      ? sameSet(filters[key] || [], val)
      : Object.is(filters[key] ?? null, val ?? null),
  );

/**
 * Patch qui remet à leur valeur par défaut les champs d'un patch donné
 * (désactivation d'un preset, retrait d'un jeton). On lit la valeur vide dans
 * LEAD_AUTO_FILTERS_DEFAULT plutôt que de la deviner par le type.
 */
export const emptyPatchFor = (patch) =>
  Object.keys(patch).reduce((acc, key) => {
    acc[key] = LEAD_AUTO_FILTERS_DEFAULT[key];
    return acc;
  }, {});
